import type { NextPage } from 'next';
import React, { useState } from 'react';

import { Badge } from '@sravni/react-design-system/lib/Badge';
import { Col, Row } from '@sravni/react-design-system/lib/Grid';
import { RadioButton } from '@sravni/react-design-system/lib/RadioButton';
import { Text, Heading } from '@sravni/react-design-system/lib/Typography';
import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { LogoBox } from '@src/components/LogoBox';
import { mfoIcons } from '@src/components/promo/icons';
import { PromoSection } from '@src/components/promo/PromoSection';
import { getSidebarBannerData } from '@src/config/getSidebarBannerData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { PageContext } from '@src/config/PageContext';
import { ListKeys } from '@src/constants/microcredits';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { Layout } from 'app/Layout';
import { SeoComponents } from 'entities/Seo/ui/SeoComponents';

import { PROMOTION_OPTIONS, PROMOTION_OPTIONS_ARRAY, PromotionCardsColSize } from './PromotionPage.config';
import styles from './PromotionPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.PROMOTION,
  source: 'search',
  seoLinksVisible: true,
  withMonetizationCardLogoClick: true,
};

export const PromotionPage: NextPage = () => {
  const { seo, hasUtmLabel } = useListPage(ListKeys.PROMOTION);
  const [sortValue, setSort] = useState(PROMOTION_OPTIONS[0].value);
  const isMobile = useIsMobile();

  const colSize = isMobile ? PromotionCardsColSize.Mobile : PromotionCardsColSize.Desktop;

  useBestProducts();

  return (
    <Layout seoJsonSchemaConfig={{ listKey: pageConfig.listKey }}>
      <div className="slimContent">
        <PageContext.Provider value={pageConfig}>
          <div className={styles.container}>
            <div className={styles.content}>
              <div className={styles.headlineContainer}>
                <Heading level={1} className={styles.headline}>
                  Акции, {isMobile ? <br /> : ''} промокоды,
                  <br />
                  скидки от МФО
                </Heading>
                <Badge color="violet" variant="primary" text="С нами выгоднее" className={styles.badge} />
                <Text className="h-color-D60">Выбирайте займы выгодно</Text>
              </div>
              <LogoBox items={[...Object.values(mfoIcons), ...Object.values(mfoIcons)]} />
              {PROMOTION_OPTIONS_ARRAY.every(({ cards }) => cards.length) && (
                <div className="h-pb-4 h-color-D10">
                  <RadioButton
                    options={PROMOTION_OPTIONS}
                    value={sortValue}
                    onChange={setSort}
                    className={styles.radio}
                  />
                </div>
              )}
              {(sortValue === 'promotion' ? PROMOTION_OPTIONS_ARRAY : PROMOTION_OPTIONS_ARRAY.slice().reverse()).map(
                ({ headline, cards }) =>
                  !!cards.length && (
                    <PromoSection headline={headline} key={headline}>
                      <Row gutter={[20, 20]}>
                        {cards.map((card) => (
                          <Col key={card.text} span={colSize}>
                            <PromoSection.Card {...card} key={card.organization} />
                          </Col>
                        ))}
                      </Row>
                    </PromoSection>
                  ),
              )}
            </div>
          </div>
          {!hasUtmLabel && seo && <SeoComponents seo={seo} breadcrumbs={seo.breadcrumbs} prefooter={seo.prefooter} />}
          <BottomBannerContainer />
        </PageContext.Provider>
      </div>
    </Layout>
  );
};

PromotionPage.getInitialProps = async (context: Application.ReduxNextPageContext) => {
  await Promise.all([getSidebarBannerData(context)]);
};
